import React from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as ArrowRight } from "@/components/common/icons/arrow-right.svg";
import Modal from "@/components/common/modal";
import ModalCloseButton from "@/components/common/modalCloseButton";
import { ReactComponent as CheckIcon } from "@/components/plan_unfurled/icons/check.svg";

interface VHTUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasVHTInProgress: boolean;
}

const VHTUpsellModal: React.FC<VHTUpsellModalProps> = ({
  isOpen,
  onClose,
  hasVHTInProgress,
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleButtonClick = () => {
    if (hasVHTInProgress) {
      navigate("/tests/");
    } else {
      navigate("/care/shop/test/product/vaginal-microbiome-test");
    }
    onClose();
  };

  const buttonClassName =
    "bg-evvy-blue flex gap-2 h-[56px] items-center justify-center min-w-[220px] px-4 py-0 w-full hover:bg-evvy-black transition-colors group";
  const buttonTextClassName =
    "font-semibold leading-[1.2] text-evvy-black group-hover:text-white text-[12px] text-center uppercase tracking-[0.6px] whitespace-pre transition-colors";
  const buttonIconClassName =
    "w-4 h-4 shrink-0 text-evvy-black group-hover:text-white transition-colors";
  const contentWrapperClassName = "flex flex-col gap-3 items-start w-full";
  const bodyTextClassName =
    "font-normal leading-[1.5] text-evvy-black text-[12px]";
  const titleClassName =
    "font-semibold leading-[1.2] text-evvy-black text-[24px] mb-0";

  const checklistItems = [
    "Uncover 700+ bacteria & fungi with one swab",
    "Get a personalized plan with next steps on treatment",
    "Option for prescription treatment (if eligible)",
    "Schedule a free 1:1 coaching call",
  ];

  return (
    <Modal closeModal={onClose} widthClass="w-full md:w-[612px]">
      <div className="bg-white flex flex-col gap-6 items-start overflow-hidden p-10 rounded-[20px] relative">
        <ModalCloseButton handleClose={onClose} />

        <div className={contentWrapperClassName}>
          {hasVHTInProgress ? (
            <>
              <p className={titleClassName}>Your Plan Will Be Available Soon</p>
              <div className={bodyTextClassName}>
                <p className="mb-0">
                  With each Vaginal Health Test, you'll receive a detailed plan
                  with next steps on treatment, symptom relief, and maintaining
                  a healthy vaginal microbiome.
                </p>
                <p className="mb-0 text-[12px]">&nbsp;</p>
                <p className="mb-0">
                  Once your full results are in, check back to view your plan.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className={titleClassName}>
                Optimize your vaginal microbiome with our{" "}
                <em className="font-semibold italic">Vaginal Health Test</em>
              </p>
              <div className={bodyTextClassName}>
                <p className="mb-0">
                  Your vaginal and urinary microbiomes are deeply connected —
                  shifts in one can influence the other, which is why
                  understanding your vaginal microbiome is a key step in getting
                  the full picture of your urinary health.
                </p>
                <p className="mb-0">&nbsp;</p>
                <p className="font-medium mb-0">
                  With each Vaginal Microbiome Test:
                </p>
              </div>
              <div className="flex flex-col gap-2 items-start w-full">
                {checklistItems.map((item, index) => (
                  <div key={index} className="flex gap-2 items-center w-full">
                    <CheckIcon className="w-3 h-3 shrink-0 [&_path:first-child]:fill-transparent [&_path:last-child]:stroke-evvy-black" />
                    <p className="font-normal leading-[1.5] text-evvy-black text-[12px] mb-0">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <button onClick={handleButtonClick} className={buttonClassName}>
          <span className={buttonTextClassName}>
            {hasVHTInProgress
              ? "Check back Soon"
              : "Test my Vaginal Microbiome"}
          </span>
          <ArrowRight className={buttonIconClassName} />
        </button>
      </div>
    </Modal>
  );
};

export default VHTUpsellModal;
